import { useFormContext } from 'react-hook-form';

import type { FormAsyncAutoCompleteProps } from '../types';
import type { FieldValues } from 'react-hook-form';

import { AsyncAutoComplete } from '@/components/AsyncAutoComplete';
import { Field, FieldDescription, FieldError } from '@/components/ui/field';
import { FormField } from '@/components/ui/form';

export const FormAsyncAutoComplete = <
  TFieldValues extends FieldValues = FieldValues,
  TItem = unknown,
  TResponse = unknown,
>(
  props: FormAsyncAutoCompleteProps<TFieldValues, TItem, TResponse>,
) => {
  const {
    name,
    label,
    description,
    isRequired,
    disabled,
    control,
    fetchUrl,
    queryKey,
    getOptionLabel,
    getOptionValue,
    transformResponse,
    emptyMessage,
    debounceMs,
    className,
  } = props;

  const formContext = useFormContext<TFieldValues>();
  const formControl = control || formContext.control;

  const fieldId = `form-field-${String(name)}`;

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field, fieldState }) => {
        const fieldLabel = (
          <>
            {label}
            {isRequired && (
              <span
                className={
                  fieldState.invalid
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                }
              >
                *
              </span>
            )}
          </>
        );

        return (
          <Field data-invalid={fieldState.invalid}>
            <AsyncAutoComplete<TItem, TResponse>
              id={fieldId}
              fetchUrl={fetchUrl}
              queryKey={queryKey}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              transformResponse={transformResponse}
              selectedValue={field.value || null}
              onSelectedValueChange={(value) => {
                field.onChange(value);
              }}
              label={fieldLabel}
              emptyMessage={emptyMessage}
              disabled={disabled}
              debounceMs={debounceMs}
              className={className}
              error={fieldState.error?.message}
            />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            {description && <FieldDescription>{description}</FieldDescription>}
          </Field>
        );
      }}
    />
  );
};
